import { QueryClient } from '@tanstack/react-query'
import { retryDelayWhileWaking, retryWhileWaking } from './waking'

/**
 * The single query client.
 *
 * Two governed rules make this load-bearing rather than incidental:
 *
 * 1. "The count and the landing list must be the same query" (design promotions
 *    010/026). A metrics tile reading 3 must land on exactly those three records.
 *    Enforce that with a SHARED QUERY KEY: the tile and the landing list are two
 *    consumers of one key, so they cannot disagree. Never compute a count from a
 *    different endpoint than the list it links to.
 *
 * 2. C-033 — records are not directly mutable. There is one mutation path in this
 *    app: draft a change candidate or set, review, approve. Approval is therefore
 *    the ONLY place cache invalidation belongs; there are no field-level writes to
 *    reconcile. Invalidate the affected lens, rail-count and metrics keys together.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Lenses are queries over an event-sourced graph (ADR-0031). Refetching is
      // cheap and correctness matters more than saved requests on an internal tool.
      staleTime: 30_000,
      /*
        ONE RETRY, EXCEPT WHEN THE ENVIRONMENT IS ASLEEP.

        `retry: 1` gave up almost immediately on the one failure that is not a
        failure: the API runs at 0 to 2 replicas, so a read after a quiet spell
        can be refused by an ingress with nothing behind it yet. Two tries later
        the shell declared the engagements unreachable and showed the error
        screen, while the replica it was waiting for came up a second afterwards.

        So the policy now asks what the error MEANS. Anything that is not a wake
        keeps the single retry it has always had; a wake is waited out. See
        `lib/waking.ts` for which failures count and how long the budget is.
      */
      retry: retryWhileWaking,
      retryDelay: retryDelayWhileWaking,
      refetchOnWindowFocus: true,
    },
  },
})

/**
 * Query keys are engagement-scoped by construction, so a cross-engagement read
 * cannot be issued by accident (C-004 scope tagging).
 */
export const queryKeys = {
  lens: (engagementId: string, subjectRef: string) =>
    ['engagement', engagementId, 'lens', subjectRef] as const,
  work: (engagementId: string, scope: 'today' | 'mine' | 'all', filter?: string) =>
    ['engagement', engagementId, 'work', scope, filter ?? null] as const,
  /** The metrics strip and its click-through landing MUST use this same key. */
  metrics: (engagementId: string, tile: string) =>
    ['engagement', engagementId, 'metrics', tile] as const,
  /** Rail attention counts; a count is a change marker, never a queue (nav-attention-counts). */
  navAttention: (engagementId: string) =>
    ['engagement', engagementId, 'nav-attention'] as const,
  /** Pending proposals. Home is the one actor surface for these (B-1 routing law). */
  proposals: (engagementId: string) =>
    ['engagement', engagementId, 'proposals'] as const,
} as const
